import React from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, FormGroup, IconButton, Link, Menu, MenuItem, Stack, Switch,
  Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { get, put, post, patch, del } from '../../utils/api';
import { downloadEventPoster, downloadEventBanner, fetchBannerHtml } from '../../utils/posterDownload';

const enc = (s) => encodeURIComponent(String(s || ''));

const STATUS_COLOR = {
  scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'default', failed: 'error',
};

// Marketing channels (columns in the schedule grid). All the social ones post via Buffer: facebook/x/
// google send the poster IMAGE; fb_video/instagram/youtube send the event's saved reel as VIDEO.
const CHANNEL_META = {
  email: { label: 'Email', Icon: MailOutlineIcon, color: '#7a1f1f' },
  whatsapp: { label: 'WhatsApp', Icon: WhatsAppIcon, color: '#25D366' },
  facebook: { label: 'Facebook', Icon: FacebookIcon, color: '#1877F2' },
  x: { label: 'X', Icon: XIcon, color: '#111111' },
  google: { label: 'Google Business', Icon: StorefrontIcon, color: '#4285F4' },
  fb_video: { label: 'FB Video', Icon: OndemandVideoIcon, color: '#1877F2' },
  instagram: { label: 'Instagram Reel', Icon: InstagramIcon, color: '#E4405F' },
  youtube: { label: 'YouTube Short', Icon: YouTubeIcon, color: '#FF0000' },
};
const CHANNEL_ORDER = ['email', 'whatsapp', 'facebook', 'x', 'google', 'fb_video', 'instagram', 'youtube'];
// Matrix channel key -> Buffer service name (and the reverse, for rendering posted records).
const SOCIAL_SERVICE = { facebook: 'facebook', x: 'twitter', google: 'googlebusiness' };
// Video channels -> Buffer service. 'fb_video' shares Buffer's 'facebook' service with the image column,
// so its posted records render under Facebook — the distinction is image vs video, not a separate channel.
const VIDEO_SERVICE = { fb_video: 'facebook', instagram: 'instagram', youtube: 'youtube' };
const SERVICE_TO_CHANNEL = { facebook: 'facebook', twitter: 'x', googlebusiness: 'google', instagram: 'instagram', youtube: 'youtube' };
const SOCIAL_STATUS_COLOR = { scheduled: 'info', posted: 'success', cancelled: 'default', failed: 'error' };

// Brand icon for a channel; dimmed when `dim` (channel not connected/enabled).
function ChannelIcon({ channel, size = 22, dim = false }) {
  const m = CHANNEL_META[channel];
  const Ico = m.Icon;
  return <Ico sx={{ fontSize: size, color: dim ? 'text.disabled' : m.color, verticalAlign: 'middle' }} />;
}

/**
 * Per-event email setup: content params + live preview + Brevo test send + scheduling.
 * All sends are safe — test goes via Brevo to the admin; scheduled sends only fire when the
 * backend's CALENDAR_EMAIL_SEND_ENABLED flag is on (Testing list only during validation).
 */
// Predefined labels for an event's additional links (QR cards + buttons). "Other" reveals a custom label.
const EXTRA_LINK_LABELS = ['Event Registration', 'RSVP', 'Vendor Booth Registration', 'Cultural Registration'];
const EXTRA_LINKS_MAX = 4;

export default function EventEmailDialog({ open, onClose, event }) {
  const eventKey = event?.event_key || '';
  const [form, setForm] = React.useState({ image_url: '', description: '', subject_override: '', beneficiaries_text: '', closing_line: '', instagram_url: '', instagram_account: 'icct', extra_links: [] });
  // Reuse an Instagram reel as a video post (FB / IG Reel / YouTube Short) with the event's WhatsApp
  // caption. The reel lives on `form` (persisted via Save content) because a schedule fires days later
  // and the worker reads it from the DB. `savedIgUrl` is the PERSISTED value — that's what gates the
  // video channels in the matrix, since an unsaved url would be invisible to the worker.
  const [savedIgUrl, setSavedIgUrl] = React.useState('');
  const [igResult, setIgResult] = React.useState(null);
  const [igPreviewOpen, setIgPreviewOpen] = React.useState(false);
  const [defaults, setDefaults] = React.useState({});
  const [previewHtml, setPreviewHtml] = React.useState('');
  const [previewSubject, setPreviewSubject] = React.useState('');
  const [previewChannel, setPreviewChannel] = React.useState('email'); // email | whatsapp | yodeck
  const [shareCode, setShareCode] = React.useState('');
  const [waCaption, setWaCaption] = React.useState('');
  const [schedules, setSchedules] = React.useState([]);
  const [socialPosts, setSocialPosts] = React.useState([]);
  const [yodeck, setYodeck] = React.useState(null); // { send_enabled, record: { enabled, status, start_date, end_date } }
  const [yodeckBusy, setYodeckBusy] = React.useState(false);
  const [yodeckEnd, setYodeckEnd] = React.useState(''); // marketing-only "show until" date (inclusive) for the TVs
  const [webSlider, setWebSlider] = React.useState(null); // { send_enabled, record, verify } — website home slider
  const [wsBusy, setWsBusy] = React.useState(false);
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  const [sendTime, setSendTime] = React.useState('09:00');
  const [tab, setTab] = React.useState(0); // 0=Content, 1=Test, 2=Schedule

  // "Browse our posts" picker for the Instagram reel URL field: our own recent IG/FB posts, so staff
  // can pick a past reel instead of hunting for it in the apps and pasting the link back.
  // Fetched only when the picker is opened (the Graph API is slow); the backend caches ~5 min and
  // `refresh` bypasses that. `loadedFor` tracks WHICH account the list belongs to, so switching
  // account reloads it rather than leaving the other account's posts on screen.
  const PICKER_PAGE_SIZE = 50;
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [recent, setRecent] = React.useState({ loading: false, posts: [], errors: null, loadedFor: null });
  const [recentCopied, setRecentCopied] = React.useState('');
  const [pickerQuery, setPickerQuery] = React.useState('');
  const [pickerPage, setPickerPage] = React.useState(1);
  const igAccount = form.instagram_account || 'icct';

  const loadRecent = React.useCallback(async (refresh = false) => {
    const acct = igAccount;
    setRecent((r) => ({ ...r, loading: true }));
    try {
      const res = await get(`/api/manage/social/recent-posts?limit=150&account=${encodeURIComponent(acct)}${refresh ? '&refresh=1' : ''}`);
      setRecent({ loading: false, posts: Array.isArray(res?.posts) ? res.posts : [], errors: res?.errors || null, loadedFor: acct });
    } catch (e) {
      setRecent({ loading: false, posts: [], errors: { load: e?.message || 'Failed to load' }, loadedFor: acct });
    }
  }, [igAccount]);

  // Load when the picker opens, and again if the account changed since the list was fetched.
  React.useEffect(() => {
    if (pickerOpen && !recent.loading && recent.loadedFor !== igAccount) loadRecent(false);
  }, [pickerOpen, igAccount, recent.loading, recent.loadedFor, loadRecent]);

  // Search across caption and URL; reset to page 1 whenever the filter changes.
  const pickerFiltered = React.useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return recent.posts;
    return recent.posts.filter((p) => `${p.name} ${p.url}`.toLowerCase().includes(q));
  }, [recent.posts, pickerQuery]);
  const pickerPages = Math.max(1, Math.ceil(pickerFiltered.length / PICKER_PAGE_SIZE));
  const pickerSafePage = Math.min(pickerPage, pickerPages);
  const pickerSlice = pickerFiltered.slice((pickerSafePage - 1) * PICKER_PAGE_SIZE, pickerSafePage * PICKER_PAGE_SIZE);
  React.useEffect(() => { setPickerPage(1); }, [pickerQuery]);

  const chooseReel = (url) => {
    setForm((f) => ({ ...f, instagram_url: url }));
    setPickerOpen(false);
  };

  const copyPostUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setRecentCopied(url);
      setTimeout(() => setRecentCopied((c) => (c === url ? '' : c)), 1800);
    } catch { /* clipboard blocked — the link is still selectable */ }
  };
  const [sendDate, setSendDate] = React.useState(''); // absolute send date (calendar wizard)
  const [editingId, setEditingId] = React.useState(null); // schedule id being date/time-edited inline
  const [editDate, setEditDate] = React.useState('');
  const [editTime, setEditTime] = React.useState('');
  const [lists, setLists] = React.useState([]);
  const [selectedLists, setSelectedLists] = React.useState([]);
  const [testEmail, setTestEmail] = React.useState('');
  const [waTo, setWaTo] = React.useState('');
  const [waNumbers, setWaNumbers] = React.useState([]);
  const [waEnabled, setWaEnabled] = React.useState(false);
  const [bufferSend, setBufferSend] = React.useState(false);
  const [bufferServices, setBufferServices] = React.useState([]);
  const [channels, setChannels] = React.useState({ email: true, whatsapp: false });
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const loadPreview = React.useCallback(async () => {
    const res = await get(`/api/manage/calendar-events/${enc(eventKey)}/email-preview`);
    setPreviewHtml(res?.html || '');
    setPreviewSubject(res?.subject || '');
    if (res?.shareCode) setShareCode(res.shareCode);
  }, [eventKey]);

  // WhatsApp caption preview — fetched lazily the first time the WhatsApp tab is opened.
  const loadWaPreview = React.useCallback(async () => {
    try {
      const res = await get(`/api/manage/calendar-events/${enc(eventKey)}/email-preview?channel=whatsapp`);
      setWaCaption(res?.caption || '');
      if (res?.shareCode) setShareCode(res.shareCode);
    } catch { setWaCaption(''); }
  }, [eventKey]);
  React.useEffect(() => { if (previewChannel === 'whatsapp' && !waCaption) loadWaPreview(); }, [previewChannel, waCaption, loadWaPreview]);

  const loadAll = React.useCallback(async () => {
    if (!eventKey) return;
    setLoading(true); setError(''); setOk('');
    try {
      const c = await get(`/api/manage/calendar-events/${enc(eventKey)}/email-content`);
      const content = c?.content || {};
      const dflt = c?.defaults || {};
      setDefaults(dflt);
      // Prefill with the saved value if present, else the resolved default (editable; clear for auto).
      setForm({
        image_url: content.image_url || dflt.image_url || '',
        description: content.description || '',
        subject_override: content.subject_override || dflt.subject || '',
        beneficiaries_text: content.beneficiaries_text || dflt.beneficiaries_text || '',
        closing_line: content.closing_line || dflt.closing_line || '',
        instagram_url: content.instagram_url || '',
        instagram_account: content.instagram_account || 'icct',
        extra_links: (Array.isArray(content.extra_links) ? content.extra_links : []).map((l) => ({
          label: String(l?.label || ''), url: String(l?.url || ''),
          custom: Boolean(l?.label) && !EXTRA_LINK_LABELS.includes(String(l.label)),
        })),
      });
      setSavedIgUrl(content.instagram_url || '');
      setTestEmail(c?.requester_email || '');
      // Mailing lists the scheduler may choose from (allowlist); pre-check the configured defaults.
      try {
        const ll = await get('/api/manage/calendar-email-lists');
        const allowed = ll?.lists || [];
        setLists(allowed);
        setSelectedLists(allowed.filter((l) => l.is_default).map((l) => String(l.id)));
      } catch { setLists([]); setSelectedLists([]); }
      // Allowed WhatsApp numbers (for the test picker).
      try {
        const wn = await get('/api/manage/whatsapp-allowed-numbers');
        setWaNumbers(wn?.numbers || []);
        setWaTo(wn?.default || (wn?.numbers || [])[0] || '');
        const waOk = Boolean(wn?.enabled) && (wn?.numbers || []).length > 0;
        setWaEnabled(waOk);
        setChannels((c) => ({ ...c, whatsapp: waOk })); // WhatsApp checked by default when configured
      } catch { setWaNumbers([]); setWaEnabled(false); }
      // Buffer social: a channel is usable only when sending is on AND that account is connected.
      try {
        const bc = await get('/api/manage/buffer/channels');
        setBufferSend(Boolean(bc?.send_enabled));
        setBufferServices((bc?.channels || []).map((c) => c.service));
      } catch { setBufferSend(false); setBufferServices([]); }
      const s = await get('/api/manage/email-schedules');
      setSchedules((s?.schedules || []).filter((x) => x.event_key === eventKey));
      try { const sp = await get(`/api/manage/calendar-events/${enc(eventKey)}/social-posts`); setSocialPosts(sp?.posts || []); } catch { setSocialPosts([]); }
      try { const y = await get(`/api/manage/calendar-events/${enc(eventKey)}/yodeck-status`); setYodeck(y || null); } catch { setYodeck(null); }
      try { const w = await get(`/api/manage/calendar-events/${enc(eventKey)}/website-slider-status`); setWebSlider(w || null); } catch { setWebSlider(null); }
      await loadPreview();
    } catch (e) {
      setError(e?.message || 'Failed to load email setup');
    } finally { setLoading(false); }
  }, [eventKey, loadPreview]);

  const loadSocialPosts = React.useCallback(async () => {
    if (!eventKey) return;
    try { const sp = await get(`/api/manage/calendar-events/${enc(eventKey)}/social-posts`); setSocialPosts(sp?.posts || []); } catch { /* keep prior */ }
  }, [eventKey]);

  React.useEffect(() => { if (open) loadAll(); }, [open, loadAll]);
  // Default the WhatsApp test recipient to the first allowed number (never leave the select blank).
  React.useEffect(() => { if (waNumbers.length && !waNumbers.includes(waTo)) setWaTo(waNumbers[0]); }, [waNumbers, waTo]);

  const onSave = async () => {
    setSaving(true); setError(''); setOk('');
    try {
      await put(`/api/manage/calendar-events/${enc(eventKey)}/email-content`, {
        image_url: form.image_url.trim(),
        description: form.description.trim(),
        subject_override: form.subject_override.trim() || null,
        beneficiaries_text: form.beneficiaries_text.trim() || null,
        closing_line: form.closing_line.trim() || null,
        instagram_url: (form.instagram_url || '').trim() || null,
        instagram_account: (form.instagram_account || '').trim() || null,
        extra_links: (form.extra_links || [])
          .map((l) => ({ label: String(l.label || '').trim(), url: String(l.url || '').trim() }))
          .filter((l) => l.label && l.url),
      });
      setSavedIgUrl((form.instagram_url || '').trim()); // now visible to the schedule worker
      setWaCaption(''); setBannerHtml(''); // invalidate lazy previews so they refetch with the new content
      await loadPreview();
      setOk('Saved. Preview updated.');
    } catch (e) { setError(e?.message || 'Failed to save'); } finally { setSaving(false); }
  };

  // Additional-links editor helpers (label dropdown + Other, and URL). Saved with the content above.
  const addExtraLink = () => setForm((f) => ({ ...f, extra_links: [...(f.extra_links || []), { label: '', url: '', custom: false }] }));
  const setExtraLink = (idx, patch) => setForm((f) => ({ ...f, extra_links: (f.extra_links || []).map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  const removeExtraLink = (idx) => setForm((f) => ({ ...f, extra_links: (f.extra_links || []).filter((_, i) => i !== idx) }));

  const onTest = async () => {
    setBusy('test'); setError(''); setOk('');
    try {
      const res = await post(`/api/manage/calendar-events/${enc(eventKey)}/email-test`, testEmail.trim() ? { email: testEmail.trim() } : {});
      setOk(`Test sent to ${res?.sent_to} (subject: ${res?.subject}).`);
    } catch (e) { setError(e?.message || 'Failed to send test'); } finally { setBusy(''); }
  };

  const onWaTest = async () => {
    setBusy('wa'); setError(''); setOk('');
    try {
      const res = await post(`/api/manage/calendar-events/${enc(eventKey)}/whatsapp-test`, waTo.trim() ? { to: waTo.trim() } : {});
      setOk(`Sent the "Confirm receipt" reminder to ${res?.sent_to}. Tap Confirm on that phone to receive the full announcement.`);
    } catch (e) { setError(e?.message || 'Failed to send WhatsApp test'); } finally { setBusy(''); }
  };

  // Store the record + Yodeck's verified truth from an action response.
  const applyYodeck = (r) => setYodeck((y) => ({ ...(y || {}), record: r?.record || null, verify: r?.verify || null }));
  // Turn Yodeck's verified state into a plain-language confirmation appended to the action message.
  const yodeckVerifyLine = (v) => {
    if (!v) return '';
    if (!v.send_enabled) return ' (server sending is off).';
    if (v.on_yodeck && v.in_playlist) return ' — confirmed on the TVs ✓';
    if (v.on_yodeck && !v.in_playlist) return ' — on Yodeck, adding to the playlist…';
    if (v.enabled && v.status === 'pending') return ` — queued${v.start_date ? ` (shows from ${v.start_date})` : ''}.`;
    if (!v.media_id) return ' — removed from the TVs (confirmed).';
    return ' — Yodeck did not confirm the media; try Resync.';
  };

  const onToggleYodeck = async (e) => {
    const enabled = e.target.checked;
    const live = Boolean(yodeck?.verify?.on_yodeck);
    if (!enabled && live && !window.confirm('Remove this poster from the Yodeck TVs now? It will stop displaying until you turn signage back on.')) return;
    setYodeckBusy(true); setError(''); setOk('');
    try {
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/yodeck-toggle`, { enabled });
      applyYodeck(r);
      setOk(`Yodeck signage ${enabled ? 'on' : 'off'}${yodeckVerifyLine(r?.verify)}`);
    } catch (err) { setError(err?.message || 'Failed to toggle Yodeck'); } finally { setYodeckBusy(false); }
  };

  const onPushYodeck = async () => {
    const mkt = Boolean(event?.marketing_only);
    if (mkt && !/^\d{4}-\d{2}-\d{2}$/.test(yodeckEnd)) { setError('Pick a "show until" date for the TVs first.'); return; }
    const pushed = Boolean(yodeck?.verify?.on_yodeck || yodeck?.record?.status === 'pushed');
    const verb = pushed ? 'Resync' : 'Push now';
    const untilTxt = mkt ? `through ${yodeckEnd}` : 'until the day after the event';
    if (!window.confirm(`${verb}: upload the latest poster to the Yodeck TVs now? It shows on the screens (${untilTxt}).`)) return;
    setYodeckBusy(true); setError(''); setOk('');
    try {
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/yodeck-push`, mkt ? { end_date: yodeckEnd } : {});
      applyYodeck(r);
      setOk(`${verb} done${yodeckVerifyLine(r?.verify)}`);
    } catch (err) { setError(err?.message || `Failed to ${verb.toLowerCase()} on Yodeck`); } finally { setYodeckBusy(false); }
  };

  // Prefill the marketing "show until" date from the current Yodeck window (stored end_date is exclusive
  // = last day + 1, so show the inclusive last day).
  React.useEffect(() => {
    if (!event?.marketing_only) return;
    // end_date may arrive as "YYYY-MM-DD" or a full ISO timestamp — take just the date part so the
    // Date() below never gets a malformed string (which would throw and blank the dialog).
    const ymd = String(yodeck?.record?.end_date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return;
    const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - 1);
    setYodeckEnd(d.toISOString().slice(0, 10));
  }, [yodeck?.record?.end_date, event?.marketing_only]);

  // --- Website home slider (SKT Nivo) — mirrors the Yodeck controls ---
  const onToggleWebSlider = async (e) => {
    const enabled = e.target.checked;
    const live = Boolean(webSlider?.verify?.on_slider);
    if (!enabled && live && !window.confirm('Remove this banner from the website slider now?')) return;
    setWsBusy(true); setError(''); setOk('');
    try {
      // Rendering the banner via APIFlash + uploading to WP can take 30-60s — allow up to 120s.
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/website-slider-toggle`, { enabled }, { timeout: 120000 });
      setWebSlider((w) => ({ ...(w || {}), record: r?.record || null, verify: r?.verify || null }));
      setOk(`Website slider ${enabled ? 'on' : 'off'}${r?.verify?.on_slider ? ' — live on the homepage ✓' : ''}`);
    } catch (err) { setError(err?.message || 'Failed to toggle website slider'); } finally { setWsBusy(false); }
  };
  const onPushWebSlider = async () => {
    const pushed = Boolean(webSlider?.verify?.on_slider || webSlider?.record?.status === 'pushed');
    const verb = pushed ? 'Resync' : 'Push now';
    if (!window.confirm(`${verb}: put this event's banner on the website home slider now?`)) return;
    setWsBusy(true); setError(''); setOk('');
    try {
      // APIFlash render + WP upload can take 30-60s — allow up to 120s (the default 30s was timing out).
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/website-slider-push`, {}, { timeout: 120000 });
      setWebSlider((w) => ({ ...(w || {}), record: r?.record || null, verify: r?.verify || null }));
      setOk(`${verb} done${r?.verify?.on_slider ? ' — live on the homepage ✓' : ''}`);
    } catch (err) { setError(err?.message || `Failed to ${verb.toLowerCase()} on the website slider`); } finally { setWsBusy(false); }
  };

  const refreshSchedules = async () => {
    const s = await get('/api/manage/email-schedules');
    setSchedules((s?.schedules || []).filter((x) => x.event_key === eventKey));
    await loadSocialPosts();
  };

  const toggleList = (id) => setSelectedLists((prev) => (
    prev.includes(String(id)) ? prev.filter((x) => x !== String(id)) : [...prev, String(id)]
  ));
  const toggleChannel = (c) => setChannels((p) => ({ ...p, [c]: !p[c] }));
  const rowChannels = (s) => (Array.isArray(s.channels) && s.channels.length ? s.channels.map(String) : ['email']);
  // A channel cell is usable when its transport is configured: WhatsApp needs an allowlist; each social
  // channel needs Buffer sending on AND that specific account connected. Video channels additionally
  // need a SAVED reel — the worker reads it from the DB when the schedule fires, days later.
  const chanEnabled = (c) => {
    if (c === 'whatsapp') return waEnabled;
    const vsvc = VIDEO_SERVICE[c];
    if (vsvc) return bufferSend && bufferServices.includes(vsvc) && Boolean(savedIgUrl);
    const svc = SOCIAL_SERVICE[c];
    if (svc) return bufferSend && bufferServices.includes(svc);
    return true; // email
  };
  // Why a video cell is unavailable — shown as the cell tooltip so the matrix is never silently dead.
  const chanReason = (c) => {
    const vsvc = VIDEO_SERVICE[c];
    if (!vsvc || chanEnabled(c)) return '';
    if (!bufferSend) return 'Buffer sending is off (BUFFER_SEND_ENABLED)';
    if (!bufferServices.includes(vsvc)) return `${vsvc} is not connected in Buffer`;
    return 'Add an Instagram reel URL on the Content tab and Save content first';
  };

  // Preview only — never posts. Fetches the reel to the events server (skipped when already stored) and
  // shows the caption exactly as it would post. Scheduling lives entirely on the Schedule tab.
  const onIgPreview = async () => {
    setBusy('igprev'); setError(''); setOk(''); setIgResult(null);
    try {
      const res = await post(`/api/manage/calendar-events/${enc(eventKey)}/social-video-from-instagram`,
        { instagramUrl: (form.instagram_url || '').trim(), account: form.instagram_account || 'icct' });
      setIgResult(res);
      setIgPreviewOpen(true);
    } catch (e) { const m = e?.message || 'Failed to prepare Instagram video'; setIgResult({ error: m }); setError(m); }
    finally { setBusy(''); }
  };

  // Toggle a channel on an already-scheduled row (matrix cell edit) — persists immediately.
  const patchRowChannels = async (s, channel) => {
    const cur = rowChannels(s);
    const next = cur.includes(channel) ? cur.filter((c) => c !== channel) : [...cur, channel];
    if (!next.length) { setError('A send must use at least one channel.'); return; }
    if (next.includes('email') && !(Array.isArray(s.list_ids) && s.list_ids.length)) {
      setError('This send has no mailing list — recreate it with Email + a list.'); return;
    }
    setError(''); setOk('');
    try {
      await patch(`/api/manage/email-schedule/${s.id}`, { channels: next });
      await refreshSchedules();
    } catch (e) { setError(e?.message || 'Failed to update channels'); }
  };

  const listLabel = React.useCallback((id) => {
    const m = lists.find((l) => String(l.id) === String(id));
    if (!m) return String(id);
    return m.count != null ? `${m.name} (${m.count})` : m.name;
  }, [lists]);

  const onSchedule = async () => {
    const chans = Object.keys(channels).filter((c) => channels[c]);
    if (!chans.length) { setError('Select at least one channel (Email / WhatsApp).'); return; }
    if (channels.email && !selectedLists.length) { setError('Select at least one mailing list for Email.'); return; }
    if (!sendDate) { setError('Pick a send date.'); return; }
    // Minimum 5-minute gap from now (so "today + current time" behaves like a safe send-now).
    const when = new Date(`${sendDate}T${sendTime || '00:00'}`);
    if (!Number.isNaN(when.getTime()) && when.getTime() < Date.now() + 5 * 60 * 1000) {
      setError('Minimum 5-minute gap needed — pick a time at least 5 minutes from now.'); return;
    }
    setBusy('schedule'); setError(''); setOk('');
    try {
      await post(`/api/manage/calendar-events/${enc(eventKey)}/email-schedule`,
        // Always schedule at the absolute picked date/time (works when a multi-day event's start day has
        // passed but a later session hasn't). days_before is sent only as a display label.
        { send_date: sendDate, send_time: sendTime, days_before: daysBeforeFromDate(sendDate), list_ids: selectedLists, channels: chans });
      await refreshSchedules();
      const chNote = chans.map((c) => CHANNEL_META[c]?.label || c).join(' + ');
      setOk(`Scheduled: ${weekdayOf(sendDate)} at ${sendTime} Central · ${chNote}.`);
    } catch (e) { setError(e?.message || 'Failed to schedule'); } finally { setBusy(''); }
  };

  const onCancel = async (id) => {
    setError(''); setOk('');
    try { await del(`/api/manage/email-schedule/${id}`); await refreshSchedules(); setOk('Schedule cancelled.'); }
    catch (e) { setError(e?.message || 'Failed to cancel'); }
  };

  // Inline date/time edit of an existing scheduled row (pencil icon).
  const startEditSchedule = (s) => { const { date, time } = centralParts(s.send_at); setEditingId(s.id); setEditDate(date); setEditTime(time); setError(''); setOk(''); };
  const cancelEditSchedule = () => { setEditingId(null); setEditDate(''); setEditTime(''); };
  const saveEditSchedule = async (s) => {
    if (!editDate) { setError('Pick a date.'); return; }
    setBusy(`edit-${s.id}`); setError(''); setOk('');
    try {
      await patch(`/api/manage/email-schedule/${s.id}`,
        { send_date: editDate, send_time: editTime || '09:00', days_before: daysBeforeFromDate(editDate) });
      await refreshSchedules();
      setOk(`Rescheduled to ${weekdayOf(editDate)} at ${editTime || '09:00'} Central.`);
      cancelEditSchedule();
    } catch (e) { setError(e?.message || 'Failed to reschedule'); } finally { setBusy(''); }
  };

  const onCancelSocial = async (id) => {
    setError(''); setOk('');
    try { await post(`/api/manage/social-posts/${id}/cancel`); await loadSocialPosts(); setOk('Social post cancelled.'); }
    catch (e) { setError(e?.message || 'Failed to cancel post'); await loadSocialPosts(); }
  };

  // Delivery-status modal: live per-channel status (GetResponse / Twilio / Buffer) for one schedule.
  const [deliveryFor, setDeliveryFor] = React.useState(null); // schedule id
  const [deliveryData, setDeliveryData] = React.useState(null);
  const [deliveryLoading, setDeliveryLoading] = React.useState(false);
  const loadDelivery = React.useCallback(async (id) => {
    setDeliveryLoading(true);
    try { const d = await get(`/api/manage/email-schedule/${id}/delivery`); setDeliveryData(d?.delivery || null); }
    catch (e) { setDeliveryData(null); setError(e?.message || 'Failed to load delivery status'); }
    finally { setDeliveryLoading(false); }
  }, []);
  const openDelivery = (id) => { setDeliveryFor(id); setDeliveryData(null); loadDelivery(id); };

  // While the dialog is open, refresh social-post status so the short cancel window stays accurate.
  React.useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => { loadSocialPosts(); }, 20000);
    return () => clearInterval(t);
  }, [open, loadSocialPosts]);

  // 1s tick to drive the "Send now" countdown while the dialog is open.
  React.useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Remaining time (m:ss) until a send-now schedule fires, or null.
  const countdown = (s) => {
    if (s.status !== 'scheduled' || !s.is_send_now) return null;
    const ms = new Date(s.send_at).getTime() - nowTick;
    if (!(ms > 0)) return 'firing…';
    const total = Math.round(ms / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };

  const [posterBusy, setPosterBusy] = React.useState(false);
  const [dlAnchor, setDlAnchor] = React.useState(null); // Download dropdown anchor
  const downloadPoster = async () => {
    setDlAnchor(null); setPosterBusy(true); setError(''); setOk('');
    try {
      await downloadEventPoster(eventKey);
      setOk('Newsletter image downloaded.');
    } catch (e) { setError(e?.message || 'Failed to download image'); } finally { setPosterBusy(false); }
  };
  const downloadBanner = async () => {
    setDlAnchor(null); setPosterBusy(true); setError(''); setOk('');
    try {
      await downloadEventBanner(eventKey);
      setOk('Website scrolling image downloaded.');
    } catch (e) { setError(e?.message || 'Failed to download image'); } finally { setPosterBusy(false); }
  };

  // Website banner preview — self-contained HTML, loaded lazily the first time the tab is opened.
  const [bannerHtml, setBannerHtml] = React.useState('');
  const [bannerLoading, setBannerLoading] = React.useState(false);
  const loadBannerPreview = React.useCallback(async () => {
    setBannerLoading(true);
    try { setBannerHtml(await fetchBannerHtml(eventKey)); }
    catch (e) { setError(e?.message || 'Failed to load banner preview'); }
    finally { setBannerLoading(false); }
  }, [eventKey]);
  // Clear the cached banner whenever the event changes so a previous event's banner never lingers —
  // the Website tab then regenerates it on the fly for the current event.
  React.useEffect(() => { setBannerHtml(''); }, [eventKey]);
  React.useEffect(() => { if (previewChannel === 'website' && !bannerHtml) loadBannerPreview(); }, [previewChannel, bannerHtml, loadBannerPreview]);

  const fmt = (iso) => { try { const d = new Date(iso); return isNaN(d) ? String(iso || '—') : d.toLocaleString(); } catch { return iso; } };


  // --- Calendar-wizard scheduling helpers (absolute send date + time picker) ---
  const eventDateStr = String(event?.event_date || '');
  const isMarketing = Boolean(event?.marketing_only);
  // Last day of the event (multi-day end, else the single day) — the schedule picker may go this far, so a
  // send can be scheduled up to the final session even after the start day has passed.
  const lastDayStr = String(event?.dates?.end || eventDateStr || '');
  // Pretty event date for the dialog title (parsed as UTC so the calendar date never shifts a day).
  const prettyEventDate = (() => {
    const [y, m, d] = eventDateStr.split('-').map(Number);
    if (!y || !m || !d) return '';
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  })();
  const pad2 = (n) => String(n).padStart(2, '0');
  const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
  // Default the new-schedule row to today + (now + 5 min), so it doubles as a safe "send now".
  React.useEffect(() => {
    if (!open) return;
    setSendDate(todayStr());
    const d = new Date(Date.now() + 5 * 60 * 1000);
    setSendTime(`${pad2(d.getHours())}:${pad2(d.getMinutes())}`);
  }, [open]);
  // whole days between a YYYY-MM-DD send date and the event date (clamped to >= 0).
  const daysBeforeFromDate = (dateStr) => {
    const [ay, am, ad] = String(dateStr || '').split('-').map(Number);
    const [ey, em, ed] = eventDateStr.split('-').map(Number);
    if (!ay || !ey) return 0;
    return Math.max(0, Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(ay, am - 1, ad)) / 86400000));
  };
  // date (YYYY-MM-DD) + time (HH:MM) of an ISO timestamp, in America/Chicago.
  const centralParts = (iso) => {
    if (!iso) return { date: '', time: '' };
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(iso));
    const g = (t) => (p.find((x) => x.type === t) || {}).value || '';
    let hh = g('hour'); if (hh === '24') hh = '00';
    return { date: `${g('year')}-${g('month')}-${g('day')}`, time: `${hh}:${g('minute')}` };
  };
  // friendly "Wednesday, Aug 12" for a YYYY-MM-DD (weekday the user asked for).
  const weekdayOf = (dateStr) => {
    const [y, m, d] = String(dateStr || '').split('-').map(Number);
    if (!y) return '';
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Default the send date/time to ~10 minutes from now (Central) when the dialog opens — leaving it
  // as-is acts as "send shortly", and it always clears the 5-minute minimum. Push the date/time out to
  // schedule for later.
  React.useEffect(() => {
    if (!open) return;
    const { date, time } = centralParts(new Date(Date.now() + 10 * 60 * 1000).toISOString());
    setSendDate(date);
    setSendTime(time);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { width: '96vw', height: '92vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        Event Marketing — {event?.title || ''}
        {isMarketing
          ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> · Marketing item</Box>
          : (prettyEventDate ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> · {prettyEventDate}</Box> : null)}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {ok ? <Alert severity="success" sx={{ mb: 1.5 }}>{ok}</Alert> : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant="fullWidth"
              sx={{
                mb: 2.5,
                minHeight: 44,
                '& .MuiTabs-flexContainer': { gap: 1 },
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minHeight: 40,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.400',
                  bgcolor: '#fff',
                  color: 'text.secondary',
                  transition: 'all .15s ease',
                  '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                },
                '& .Mui-selected': {
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  bgcolor: '#eef4ff',
                  boxShadow: '0 1px 5px rgba(25,118,210,.18)',
                },
              }}
            >
              <Tab icon={<ArticleOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Content" />
              <Tab icon={<SendOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Test" />
              <Tab icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Schedule" />
            </Tabs>

            {tab === 0 && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {/* Left: content fields */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Content</Typography>
                <Stack spacing={1.5}>
                  <TextField label="Event image URL" size="small" fullWidth value={form.image_url}
                    onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                    placeholder={defaults.image_url || 'https://…'}
                    helperText="Prefilled from the mapped product image; clear to use it automatically." />
                  <TextField label="Importance of the Festival/Event (description)" size="small" fullWidth multiline minRows={4}
                    value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                  <TextField label="Subject (optional override)" size="small" fullWidth value={form.subject_override}
                    onChange={(e) => setForm((f) => ({ ...f, subject_override: e.target.value }))}
                    placeholder={defaults.subject || "{date}: {event name}"}
                    helperText="Prefilled with the auto subject; clear to keep it automatic." />
                  <TextField label="Beneficiaries (optional override)" size="small" fullWidth value={form.beneficiaries_text}
                    onChange={(e) => setForm((f) => ({ ...f, beneficiaries_text: e.target.value }))}
                    placeholder={defaults.beneficiaries_text || 'No linked Poshaka product'}
                    helperText="Prefilled from Poshaka benefit sets; clear to keep it automatic." />
                  <TextField label="Closing line (optional override)" size="small" fullWidth multiline minRows={2}
                    value={form.closing_line}
                    onChange={(e) => setForm((f) => ({ ...f, closing_line: e.target.value }))}
                    placeholder={defaults.closing_line || 'All are welcome…'}
                    helperText="Clear to use the default closing line." />

                  {/* Additional links (Registration/RSVP/etc.) → QR card + "Click here" button on the
                      banner, event page, Yodeck poster and email. Saved with the content. */}
                  <Divider sx={{ my: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Additional links (QR + button)</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Each shows as a QR code + “Click here” button on the banner, event page, Yodeck and email. Up to {EXTRA_LINKS_MAX}. Save, then refresh the previews and Resync/Push.
                    </Typography>
                    <Stack spacing={1}>
                      {(form.extra_links || []).map((row, idx) => {
                        const selectVal = row.custom ? 'Other' : (EXTRA_LINK_LABELS.includes(row.label) ? row.label : '');
                        return (
                          <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <TextField select size="small" label="Label" value={selectVal} sx={{ minWidth: 190 }}
                              onChange={(e) => { const v = e.target.value; setExtraLink(idx, v === 'Other' ? { custom: true, label: '' } : { custom: false, label: v }); }}>
                              <MenuItem value=""><em>Choose…</em></MenuItem>
                              {EXTRA_LINK_LABELS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                              <MenuItem value="Other">Other…</MenuItem>
                            </TextField>
                            {row.custom ? (
                              <TextField size="small" label="Custom label" value={row.label} sx={{ width: 170 }}
                                inputProps={{ maxLength: 40 }}
                                onChange={(e) => setExtraLink(idx, { label: e.target.value })} />
                            ) : null}
                            <TextField size="small" label="Link URL" value={row.url} fullWidth placeholder="https://…"
                              onChange={(e) => setExtraLink(idx, { url: e.target.value })} />
                            <Tooltip title="Remove link"><IconButton size="small" color="error" onClick={() => removeExtraLink(idx)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        );
                      })}
                    </Stack>
                    <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled={(form.extra_links || []).length >= EXTRA_LINKS_MAX}
                      onClick={addExtraLink}>+ Add link</Button>
                  </Box>

                  {/* Source reel for the video channels. Saved with the content because a schedule fires
                      days later and the worker reads the reel from the DB, not from this dialog. */}
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField label="Instagram reel URL (for video channels)" size="small" fullWidth
                      value={form.instagram_url || ''}
                      onChange={(e) => setForm((f) => ({ ...f, instagram_url: e.target.value }))}
                      placeholder="https://www.instagram.com/reel/…"
                      helperText={(
                        <>
                          Our own IG post/reel. Required to enable the FB Video / Instagram / YouTube columns on
                          Schedule.{' '}
                          <Link component="button" type="button" underline="always"
                            onClick={(e) => { e.preventDefault(); setPickerOpen(true); }}
                            sx={{ verticalAlign: 'baseline', fontSize: 'inherit' }}>
                            Browse our posts
                          </Link>
                        </>
                      )} />
                    <TextField select label="Account" size="small" sx={{ minWidth: 130 }}
                      value={form.instagram_account || 'icct'}
                      onChange={(e) => setForm((f) => ({ ...f, instagram_account: e.target.value }))}>
                      <MenuItem value="icct">ICCT</MenuItem>
                      <MenuItem value="kotilinga">Kotilinga</MenuItem>
                    </TextField>
                  </Stack>

                  <Box>
                    <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save content'}</Button>
                  </Box>
                </Stack>
              </Box>

              {/* Right: preview */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Preview</Typography>
                  <ToggleButtonGroup size="small" exclusive value={previewChannel} onChange={(_e, v) => v && setPreviewChannel(v)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, py: 0.6, gap: 0.6, fontSize: '0.85rem', fontWeight: 600 } }}>
                    <ToggleButton value="email"><ChannelIcon channel="email" size={18} /> Email</ToggleButton>
                    <ToggleButton value="whatsapp"><ChannelIcon channel="whatsapp" size={18} /> WhatsApp</ToggleButton>
                    <ToggleButton value="yodeck"><Box component="img" src="/assets/images/yodeck.png" alt="" sx={{ height: 14, width: 'auto' }} /> Yodeck</ToggleButton>
                    <ToggleButton value="website"><LanguageIcon sx={{ fontSize: 18 }} /> Website</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button size="small" variant="outlined" onClick={(e) => setDlAnchor(e.currentTarget)} disabled={posterBusy} endIcon={<ArrowDropDownIcon />}>
                    {posterBusy ? 'Generating…' : 'Download'}
                  </Button>
                  <Menu anchorEl={dlAnchor} open={Boolean(dlAnchor)} onClose={() => setDlAnchor(null)}>
                    <MenuItem onClick={downloadPoster}>Newsletter</MenuItem>
                    <MenuItem onClick={downloadBanner}>Website Scrolling Image</MenuItem>
                  </Menu>
                  <Tooltip title="Refresh preview"><IconButton size="small" onClick={loadPreview}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>

                {previewChannel === 'email' && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Subject: {previewSubject}</Typography>
                    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', height: 620, bgcolor: '#fff' }}>
                      <iframe title="Email preview" srcDoc={previewHtml} style={{ width: '100%', height: '100%', border: 0 }} />
                    </Box>
                  </>
                )}

                {previewChannel === 'yodeck' && (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', height: 620, bgcolor: '#fff', display: 'flex', justifyContent: 'center' }}>
                    {shareCode
                      ? <iframe title="Yodeck preview" src={`/e/${enc(shareCode)}?yodeck=1`} style={{ width: '100%', height: '100%', border: 0 }} />
                      : <Typography variant="caption" color="text.secondary" sx={{ m: 'auto' }}>Save content to generate the signage preview.</Typography>}
                  </Box>
                )}

                {previewChannel === 'whatsapp' && (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'auto', height: 620, bgcolor: '#e5ddd5', p: 2 }}>
                    <Box sx={{ maxWidth: 340, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,.2)', p: 1, ml: 0 }}>
                      {(form.image_url || defaults.image_url) ? (
                        <Box component="img" src={form.image_url || defaults.image_url} alt="" sx={{ width: '100%', borderRadius: 1.5, display: 'block', mb: 0.5 }} />
                      ) : null}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                        {waCaption || 'Loading…'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      The actual send attaches the rendered poster image; this shows the caption + hero image.
                    </Typography>
                  </Box>
                )}

                {previewChannel === 'website' && (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', height: 620, bgcolor: '#faf7f2', display: 'flex', flexDirection: 'column' }}>
                    {bannerLoading && !bannerHtml ? (
                      <Box sx={{ m: 'auto' }}><CircularProgress size={24} /></Box>
                    ) : bannerHtml ? (
                      <>
                        {/* The banner is a fixed 1800×720 doc; scale it to the pane width so it reads as the homepage strip. */}
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1800 / 720', boxShadow: '0 2px 10px rgba(0,0,0,.15)', borderRadius: 1, overflow: 'hidden' }}>
                            <iframe
                              title="Website banner preview"
                              srcDoc={bannerHtml}
                              scrolling="no"
                              style={{ position: 'absolute', top: 0, left: 0, width: 1800, height: 720, border: 0, transformOrigin: 'top left', transform: 'scale(0.3)' }}
                              ref={(el) => {
                                if (!el || !el.parentElement) return;
                                const set = () => { const w = el.parentElement.clientWidth; if (w) el.style.transform = `scale(${w / 1800})`; };
                                set(); requestAnimationFrame(set); setTimeout(set, 200);
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>
                          Landscape banner for the website home-page scroller. Use Download → Website Scrolling Image to save it.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ m: 'auto' }}>Save content to generate the website banner.</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={2} sx={{ maxWidth: 680 }}>
                <Typography variant="body2" color="text.secondary">
                  Send yourself a test to preview on a real device. These never reach your lists.
                </Typography>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <ChannelIcon channel="email" size={20} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Email test</Typography>
                    <Chip size="small" label="Brevo" variant="outlined" sx={{ ml: 'auto' }} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" label="Send to (blank = you)" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} sx={{ flex: 1 }} />
                    <Button size="small" variant="contained" onClick={onTest} disabled={busy === 'test'}>{busy === 'test' ? 'Sending…' : 'Send'}</Button>
                  </Stack>
                </Box>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <ChannelIcon channel="whatsapp" size={20} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>WhatsApp test</Typography>
                    <Chip size="small" label="Twilio" variant="outlined" sx={{ ml: 'auto' }} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {waNumbers.length > 0 ? (
                      <TextField select size="small" label="Send to (allowed numbers)" value={waTo} onChange={(e) => setWaTo(e.target.value)} sx={{ flex: 1 }}>
                        {waNumbers.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </TextField>
                    ) : (
                      <TextField size="small" label="Number, E.164" placeholder="+19015551234" value={waTo} onChange={(e) => setWaTo(e.target.value)} sx={{ flex: 1 }} />
                    )}
                    <Button size="small" variant="contained" color="success" onClick={onWaTest} disabled={busy === 'wa' || (waNumbers.length > 0 && !waTo)}>{busy === 'wa' ? 'Sending…' : 'Send'}</Button>
                  </Stack>
                  {waNumbers.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No allowed numbers configured (set WHATSAPP_ALLOWED_NUMBERS).
                    </Typography>
                  )}
                </Box>

                {/* Reuse the event's saved Instagram reel as a video post, with the WhatsApp caption. */}
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <ChannelIcon channel="instagram" size={20} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Reuse Instagram reel → FB / Instagram / YouTube</Typography>
                    <Chip size="small" label="Buffer" variant="outlined" sx={{ ml: 'auto' }} />
                  </Stack>

                  {!(form.instagram_url || '').trim() ? (
                    <Typography variant="caption" color="text.secondary">
                      Add an <strong>Instagram reel URL</strong> on the Content tab (and Save content) to use this.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {(form.instagram_url || '').trim()} · <strong>{form.instagram_account || 'icct'}</strong>
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={onIgPreview} disabled={busy === 'igprev'}>
                          {busy === 'igprev' ? 'Preparing…' : 'Preview'}
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Fetches the reel to the events server (only the first time) and shows the post content.
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}

            {tab === 2 && (
            <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Schedule</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            {/* Yodeck signage — single auto-created entry per event; on/off + Push/Resync, all confirmed against Yodeck. */}
            {(() => {
              const rec = yodeck?.record;
              const v = yodeck?.verify;
              const sendOn = Boolean(yodeck?.send_enabled);
              const on = Boolean(rec?.enabled);
              const live = Boolean(v?.on_yodeck && v?.in_playlist); // verified on the TVs right now
              const start = v?.start_date || (rec?.start_date && String(rec.start_date).slice(0, 10));
              const end = v?.end_date || (rec?.end_date && String(rec.end_date).slice(0, 10));
              const win = start ? `${start} → ${end || ''}` : '';
              let statusLabel = 'loading…';
              let statusChipColor = 'default';
              if (!yodeck) statusLabel = 'loading…';
              else if (!sendOn) statusLabel = 'Off (server)';
              else if (!rec) statusLabel = 'not set up';
              else if (!on) statusLabel = 'Off';
              else if (live) { statusLabel = 'On the TVs'; statusChipColor = 'success'; }
              else if (v?.on_yodeck && !v?.in_playlist) { statusLabel = 'Resync needed'; statusChipColor = 'warning'; }
              else if (rec.status === 'pushed' && v && !v.on_yodeck) { statusLabel = 'Resync needed'; statusChipColor = 'warning'; }
              else if (rec.status === 'failed') { statusLabel = 'Failed'; statusChipColor = 'error'; }
              else if (rec.status === 'expired') statusLabel = 'Ended';
              else { statusLabel = 'Queued'; statusChipColor = 'info'; }
              const isPushed = live || rec?.status === 'pushed';
              return (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box component="img" src="/assets/images/yodeck.png" alt="Yodeck" sx={{ height: 16, width: 'auto' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Yodeck signage</Typography>
                    <Chip size="small" label={yodeckBusy ? 'working…' : statusLabel} color={statusChipColor} variant="outlined" sx={{ ml: 'auto' }} />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Switch size="small" color="success" checked={on} disabled={!sendOn || !rec || yodeckBusy} onChange={onToggleYodeck} />
                    <Typography variant="body2">Show this poster on the temple TVs</Typography>
                    {on && sendOn && rec ? (
                      <Button size="small" variant="outlined" onClick={onPushYodeck} disabled={yodeckBusy} sx={{ ml: 'auto', textTransform: 'none' }}>
                        {isPushed ? 'Resync' : 'Push now'}
                      </Button>
                    ) : null}
                  </Stack>
                  {event?.marketing_only && sendOn && rec ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <TextField label="Show on TVs until" type="date" size="small" value={yodeckEnd}
                        onChange={(e) => setYodeckEnd(e.target.value)} InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().slice(0, 10) }} sx={{ width: 190 }} />
                      <Typography variant="caption" color="text.secondary">Pick the last day to display, then Push/Resync.</Typography>
                    </Stack>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {event?.marketing_only
                      ? `Shows on the TVs from today through the date you pick above${win && on ? ` (currently until ${end})` : ''}. Turn off to remove it; Push/Resync applies that date.`
                      : `Appears automatically ~2 weeks before the event${win && on ? ` (${start} → ${end})` : ''}. Turn off to remove it; Resync re-uploads the latest poster.`}
                  </Typography>
                  {!sendOn ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                      Signage sending is off on this server (dev) — this activates in production.
                    </Typography>
                  ) : null}
                </>
              );
            })()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            {/* Website home slider — auto-created per event; on/off + Push/Resync, confirmed against WordPress. */}
            {(() => {
              const rec = webSlider?.record;
              const v = webSlider?.verify;
              const sendOn = Boolean(webSlider?.send_enabled);
              const on = Boolean(rec?.enabled);
              const live = Boolean(v?.on_slider);
              const start = v?.start_date || (rec?.start_date && String(rec.start_date).slice(0, 10));
              const end = v?.end_date || (rec?.end_date && String(rec.end_date).slice(0, 10));
              let statusLabel = 'loading…';
              let color = 'default';
              if (!webSlider) statusLabel = 'loading…';
              else if (!sendOn) statusLabel = 'Off (server)';
              else if (!rec) statusLabel = 'not set up';
              else if (!on) statusLabel = 'Off';
              else if (live) { statusLabel = 'On the homepage'; color = 'success'; }
              else if (rec.status === 'pushed' && v && !v.on_slider) { statusLabel = 'Resync needed'; color = 'warning'; }
              else if (rec.status === 'failed') { statusLabel = 'Failed'; color = 'error'; }
              else if (rec.status === 'expired') statusLabel = 'Ended';
              else { statusLabel = 'Queued'; color = 'info'; }
              const isPushed = live || rec?.status === 'pushed';
              return (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <LanguageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Website slider</Typography>
                    <Chip size="small" label={wsBusy ? 'working…' : statusLabel} color={color} variant="outlined" sx={{ ml: 'auto' }} />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Switch size="small" color="success" checked={on} disabled={!sendOn || !rec || wsBusy} onChange={onToggleWebSlider} />
                    <Typography variant="body2">Show this banner on the website home page</Typography>
                    {on && sendOn && rec ? (
                      <Button size="small" variant="outlined" onClick={onPushWebSlider} disabled={wsBusy} sx={{ ml: 'auto', textTransform: 'none' }}>
                        {isPushed ? 'Resync' : 'Push now'}
                      </Button>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Appears automatically ~1 week before the event{start && on ? ` (${start} → ${end})` : ''}, and is removed after. The slide links to the event page.
                  </Typography>
                  {!sendOn ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                      Website slider sending is off on this server (dev) — this activates in production.
                    </Typography>
                  ) : null}
                </>
              );
            })()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            {/* Email channel target (lists) — one per line. */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <ChannelIcon channel="email" size={16} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Email lists</Typography>
            </Stack>
            <Stack spacing={0.25}>
              {lists.length === 0 ? <Typography variant="caption" color="text.secondary">none available</Typography> : lists.map((l) => (
                <FormControlLabel key={l.id} sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={selectedLists.includes(String(l.id))} onChange={() => toggleList(l.id)} />}
                  label={<Typography variant="body2">{l.name} ({l.count != null ? l.count : '?'}){l.is_default ? ' · default' : ''}</Typography>} />
              ))}
            </Stack>
            </Box>
            </Stack>

            {/* Matrix: dates × channels — toggle any cell to turn a channel on/off for that date. */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Scheduled sends</Typography>
              {/* 8 channel columns need ~1100px; scroll the grid itself rather than let it blow out the
                  dialog on narrower laptops (<=1024px). */}
              <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1100, '& td, & th': { px: 1, py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  {CHANNEL_ORDER.map((c) => (
                    <TableCell key={c} align="center" sx={{ fontWeight: 700 }}>
                      <Tooltip title={CHANNEL_META[c].label + (chanEnabled(c) ? '' : ` · ${chanReason(c) || 'not configured'}`)}>
                        <span><ChannelIcon channel={c} size={26} dim={!chanEnabled(c)} /></span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((s) => {
                  const chs = rowChannels(s);
                  const editable = s.status === 'scheduled' && !s.is_pub_review;
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        {editingId === s.id ? (
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 0.25 }}>
                            <TextField type="date" size="small" label="Date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                              InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr(), max: isMarketing ? undefined : lastDayStr }} sx={{ width: 160 }} />
                            <TextField type="time" size="small" label="Time (CT)" value={editTime} onChange={(e) => setEditTime(e.target.value)}
                              InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                          </Stack>
                        ) : (
                          <>
                            <Typography variant="body2">{s.is_pub_review ? 'Publication review' : s.is_send_now ? 'Send now' : `${s.days_before}d before`}</Typography>
                            <Typography variant="caption" color="text.secondary">{fmt(s.send_at)}{s.error_text ? ` · ${s.error_text}` : ''}</Typography>
                            {countdown(s) && <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'warning.main' }}>⏱ fires in {countdown(s)}</Typography>}
                          </>
                        )}
                      </TableCell>
                      {CHANNEL_ORDER.map((c) => {
                        const enabled = chanEnabled(c);
                        return (
                          <TableCell key={c} align="center">
                            <Tooltip title={enabled ? '' : chanReason(c)}>
                              <span>
                                <Switch color="success"
                                  checked={chs.includes(c)} disabled={!editable || !enabled}
                                  onChange={() => patchRowChannels(s, c)} />
                              </span>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <Tooltip title="View delivery status (per channel)">
                          <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" clickable onClick={() => openDelivery(s.id)} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {editingId === s.id ? (
                          <Stack direction="row" spacing={0} justifyContent="flex-end">
                            <Tooltip title="Save date & time"><span><IconButton size="small" color="primary" disabled={busy === `edit-${s.id}`} onClick={() => saveEditSchedule(s)}><CheckIcon fontSize="small" /></IconButton></span></Tooltip>
                            <Tooltip title="Discard"><IconButton size="small" onClick={cancelEditSchedule}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        ) : s.status === 'scheduled' ? (
                          <Stack direction="row" spacing={0} justifyContent="flex-end">
                            {(!s.is_send_now && !s.is_pub_review) ? (
                              <Tooltip title="Edit date & time"><IconButton size="small" onClick={() => startEditSchedule(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            ) : null}
                            <Tooltip title="Cancel this schedule"><IconButton size="small" color="error" onClick={() => onCancel(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Add-a-date row */}
                <TableRow sx={{ '& td': { borderTop: '2px solid #eee' } }}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={1} sx={{ pt: 0.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField type="date" size="small" label="Send date" value={sendDate} onChange={(e) => setSendDate(e.target.value)}
                          InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr(), max: isMarketing ? undefined : lastDayStr }} sx={{ width: 165 }} />
                        <TextField type="time" size="small" label="Time (Central)" value={sendTime} onChange={(e) => setSendTime(e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                      </Stack>
                      {sendDate ? (
                        <Typography variant="caption" color="text.secondary">
                          {isMarketing
                            ? `${weekdayOf(sendDate)} · min 5 min from now`
                            : `${weekdayOf(sendDate)} · ${daysBeforeFromDate(sendDate) === 0 ? 'today' : `${daysBeforeFromDate(sendDate)} day${daysBeforeFromDate(sendDate) === 1 ? '' : 's'} before the event`} · min 5 min from now`}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  {CHANNEL_ORDER.map((c) => {
                    const enabled = chanEnabled(c);
                    return (
                      <TableCell key={c} align="center" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                        <Tooltip title={enabled ? '' : chanReason(c)}>
                          <span>
                            <Switch color="success"
                              checked={!!channels[c] && enabled} disabled={!enabled} onChange={() => toggleChannel(c)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ verticalAlign: 'top', pt: 2 }}><Typography variant="caption" color="text.secondary">new</Typography></TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                    <Button size="small" variant="contained"
                      onClick={onSchedule}
                      sx={{ whiteSpace: 'nowrap' }}
                      disabled={busy === 'schedule' || (!isMarketing && schedules.filter((s) => ['scheduled', 'sending'].includes(s.status) && !s.is_pub_review).length >= 10)}>
                      {busy === 'schedule' ? '…' : 'Schedule'}
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Up to 10 active schedules. Set the date &amp; time (defaults to now + 5 min), toggle the channels, then Schedule.
              </Typography>
            </Box>

            {/* Social posts (Buffer): per-post status + cancel (only during the ~5-min window before publish). */}
            {socialPosts.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Social posts (Buffer)</Typography>
                  <Tooltip title="Refresh status"><IconButton size="small" onClick={loadSocialPosts}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Each post publishes ~5 min after its schedule fires — cancel here within that window. Once posted, it&apos;s live and can&apos;t be recalled.
                </Typography>
                <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5 }, '& tbody tr:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Scheduled for</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {socialPosts.map((p) => {
                      const chKey = SERVICE_TO_CHANNEL[p.service] || p.service;
                      const meta = CHANNEL_META[chKey];
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {meta ? <ChannelIcon channel={chKey} size={18} /> : null}
                              <Typography variant="body2">{meta ? meta.label : p.service}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{p.due_at || '—'}</Typography></TableCell>
                          <TableCell>
                            <Chip size="small" label={p.status} color={SOCIAL_STATUS_COLOR[p.status] || 'default'} variant="outlined" />
                            {p.error ? <Typography variant="caption" color="error" sx={{ display: 'block' }}>{p.error}</Typography> : null}
                          </TableCell>
                          <TableCell align="right">
                            {p.cancellable ? (
                              <Button size="small" color="error" variant="outlined" onClick={() => onCancelSocial(p.id)} sx={{ minWidth: 0, px: 1 }}>Cancel</Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
            </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Delivery status — live per-channel status + hotlinks for one schedule. */}
      <Dialog open={deliveryFor != null} onClose={() => setDeliveryFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 9 }}>
          Delivery status
          <Tooltip title="Refresh"><span>
            <IconButton onClick={() => loadDelivery(deliveryFor)} disabled={deliveryLoading} sx={{ position: 'absolute', right: 48, top: 8 }}><RefreshIcon /></IconButton>
          </span></Tooltip>
          <IconButton onClick={() => setDeliveryFor(null)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {deliveryLoading && !deliveryData ? (
            <Stack direction="row" justifyContent="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
          ) : !deliveryData ? (
            <Typography variant="body2" color="text.secondary">No delivery info.</Typography>
          ) : (
            <Stack spacing={2}>
              {deliveryData.email && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ChannelIcon channel="email" size={18} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Email (GetResponse)</Typography>
                    <Chip size="small" label={deliveryData.email.status} color="info" variant="outlined" />
                    {deliveryData.email.link && <Link href={deliveryData.email.link} target="_blank" rel="noopener" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center' }}><OpenInNewIcon fontSize="small" /></Link>}
                  </Stack>
                  {deliveryData.email.statistics && <Typography variant="caption" color="text.secondary">{JSON.stringify(deliveryData.email.statistics)}</Typography>}
                  {deliveryData.email.error && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{deliveryData.email.error}</Typography>}
                </Box>
              )}
              {(deliveryData.whatsapp || []).length > 0 && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <ChannelIcon channel="whatsapp" size={18} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>WhatsApp (Twilio)</Typography>
                  </Stack>
                  {deliveryData.whatsapp.map((w, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                      <Typography variant="body2" sx={{ minWidth: 130 }}>{w.to}</Typography>
                      <Chip size="small" variant="outlined" label={w.status}
                        color={['delivered', 'read'].includes(w.status) ? 'success' : ['failed', 'undelivered'].includes(w.status) ? 'error' : 'default'} />
                      {w.error && <Typography variant="caption" color="error">{w.error}</Typography>}
                      {w.link && <Link href={w.link} target="_blank" rel="noopener" sx={{ ml: 'auto', display: 'inline-flex' }}><OpenInNewIcon fontSize="small" /></Link>}
                    </Stack>
                  ))}
                </Box>
              )}
              {(deliveryData.social || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Social (Buffer)</Typography>
                  {deliveryData.social.map((s, i) => {
                    const chKey = SERVICE_TO_CHANNEL[s.service] || s.service;
                    const meta = CHANNEL_META[chKey];
                    return (
                      <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                        {meta ? <ChannelIcon channel={chKey} size={18} /> : null}
                        <Typography variant="body2" sx={{ minWidth: 120 }}>{meta ? meta.label : s.service}</Typography>
                        <Chip size="small" variant="outlined" label={s.status}
                          color={SOCIAL_STATUS_COLOR[s.status] || (s.status === 'sent' ? 'success' : 'default')} />
                        {s.error && <Typography variant="caption" color="error">{s.error}</Typography>}
                        {s.link && <Link href={s.link} target="_blank" rel="noopener" sx={{ ml: 'auto', display: 'inline-flex' }}><OpenInNewIcon fontSize="small" /></Link>}
                      </Stack>
                    );
                  })}
                </Box>
              )}
              {!deliveryData.email && !(deliveryData.whatsapp || []).length && !(deliveryData.social || []).length && (
                <Typography variant="body2" color="text.secondary">No messages recorded for this schedule yet.</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDeliveryFor(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Reel preview: the caption exactly as it would post, plus where the mp4 was stored. */}
      <Dialog open={igPreviewOpen} onClose={() => setIgPreviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Reel preview
          <IconButton onClick={() => setIgPreviewOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {igResult && !igResult.error && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Post content</Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {(((igResult.results || []).find((r) => r.would_post && r.would_post.text) || {}).would_post || {}).text
                      || igResult.caption || ''}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Video {igResult.rehosted?.reused ? '(already on the events server — not re-downloaded)' : '(fetched to the events server)'}
                  {' · '}{Math.round((igResult.rehosted?.bytes || 0) / 1024 / 1024 * 10) / 10} MB
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {igResult.rehosted?.path}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                  <a href={igResult.rehosted?.url} target="_blank" rel="noreferrer">{igResult.rehosted?.url}</a>
                </Typography>
              </Box>
            </Stack>
          )}
          {igResult && igResult.error && <Alert severity="error">{igResult.error}</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => setIgPreviewOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Post picker for the reel field: our own recent Instagram posts, searchable + paged. Choosing
          one fills the reel URL and closes. Instagram only — the video pipeline resolves the mp4 from
          the IG Graph API by shortcode, so no other platform's URL can work here. */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InstagramIcon sx={{ fontSize: 20 }} />
            <span>Our recent Instagram posts</span>
            <Chip size="small" variant="outlined" label={igAccount === 'kotilinga' ? 'Kotilinga' : 'ICCT'} />
            <Tooltip title="Reload from Instagram/Facebook">
              <span>
                <IconButton size="small" onClick={() => loadRecent(true)} disabled={recent.loading} sx={{ ml: 'auto' }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small" fullWidth autoFocus
            placeholder="Search caption or link…"
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            sx={{ mb: 1.5 }}
          />

          {recent.loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading posts…</Typography>
            </Stack>
          )}

          {!recent.loading && recent.errors && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {Object.entries(recent.errors).map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </Alert>
          )}

          {!recent.loading && !pickerFiltered.length && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {recent.posts.length ? 'No posts match that search.' : 'No posts found.'}
            </Typography>
          )}

          {!recent.loading && !!pickerSlice.length && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {pickerSlice.map((p) => {
                const selected = form.instagram_url === p.url;
                return (
                  <Stack
                    key={p.id}
                    direction="row" spacing={1} alignItems="center"
                    sx={{ px: 1.25, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 },
                      bgcolor: selected ? 'action.selected' : undefined }}
                  >
                    <InstagramIcon sx={{ fontSize: 17, color: '#C13584', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', flexShrink: 0 }}>
                      {p.date}
                    </Typography>
                    <Typography variant="body2" title={p.name}
                      sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                    <Button size="small" variant={selected ? 'contained' : 'outlined'}
                      sx={{ flexShrink: 0, minWidth: 0, px: 1.2, fontSize: 11 }}
                      onClick={() => chooseReel(p.url)}>
                      {selected ? 'Selected' : 'Use'}
                    </Button>
                    <Tooltip title={recentCopied === p.url ? 'Copied!' : 'Copy link'}>
                      <IconButton size="small" onClick={() => copyPostUrl(p.url)} sx={{ flexShrink: 0 }}>
                        {recentCopied === p.url
                          ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open post">
                      <IconButton size="small" component="a" href={p.url} target="_blank" rel="noreferrer" sx={{ flexShrink: 0 }}>
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {pickerFiltered.length
              ? `${(pickerSafePage - 1) * PICKER_PAGE_SIZE + 1}–${Math.min(pickerSafePage * PICKER_PAGE_SIZE, pickerFiltered.length)} of ${pickerFiltered.length}`
              : ''}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {pickerPages > 1 && (
              <>
                <Button size="small" disabled={pickerSafePage <= 1} onClick={() => setPickerPage((n) => Math.max(1, n - 1))}>Prev</Button>
                <Typography variant="caption" color="text.secondary">Page {pickerSafePage} / {pickerPages}</Typography>
                <Button size="small" disabled={pickerSafePage >= pickerPages} onClick={() => setPickerPage((n) => Math.min(pickerPages, n + 1))}>Next</Button>
              </>
            )}
            <Button onClick={() => setPickerOpen(false)}>Close</Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
